 
import {
  Box,
  Typography,
  Card,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useCollection } from '../hooks/useFirestore';
import { query as fbQuery, orderBy as fbOrderBy } from 'firebase/firestore';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { db } from '../firebase';
import { deleteDoc, doc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

interface Notice {
  id: string;
  title: string;
  content: string;
  // legacy
  target?: 'school' | 'student' | 'both' | 'lp';
  // new
  targets?: string[];
  publishedAt: Date;
  expiresAt?: Date;
  createdAt: Date;
}

const TARGET_LABELS: Record<string, string> = {
  school: '教室',
  student: '生徒',
  lp: 'LP',
};

function uniq(arr: string[]): string[] {
  return Array.from(new Set(arr.filter(Boolean)));
}

function resolveTargets(n: any): string[] {
  const v = n?.targets;
  if (Array.isArray(v)) return uniq(v.map(String));
  const legacy = String(n?.target || '');
  if (!legacy) return ['school', 'student'];
  if (legacy === 'both') return ['school', 'student'];
  return [legacy];
}

function toDateOrNull(value: any): Date | null {
  if (!value) return null;
  try {
    const d = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
}

function formatDateTime(value: any, fallback: string): string {
  const d = toDateOrNull(value);
  if (!d) return fallback;
  try {
    return format(d, 'yyyy/MM/dd HH:mm', { locale: ja });
  } catch {
    return fallback;
  }
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { data: notices } = useCollection<Notice>(
    'notices',
    (q) => fbQuery(q, fbOrderBy('publishedAt', 'desc'))
  );

  const handleDelete = async (noticeId: string) => {
    if (!window.confirm('このお知らせを削除してもよろしいですか？')) return;

    try {
      await deleteDoc(doc(db, 'notices', noticeId));
    } catch (error) {
      console.error('Failed to delete notice:', error);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">お知らせ管理</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/notifications/new')}
        >
          新規作成
        </Button>
      </Box>

      <Card>
        <List>
          {notices?.map((notice) => (
            <ListItem
              key={notice.id}
              secondaryAction={
                <Box>
                  <IconButton
                    edge="end"
                    onClick={() => navigate(`/notifications/${notice.id}/edit`)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDelete(notice.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              }
            >
              <ListItemText
                primary={notice.title}
                secondary={`表示対象: ${(() => {
                  const t = resolveTargets(notice);
                  if (!t || t.length === 0) return '未設定';
                  return t.map((x) => TARGET_LABELS[x] || x).join(' / ');
                })()} / ${formatDateTime((notice as any).publishedAt, '未設定')} - ${formatDateTime((notice as any).expiresAt, '期限なし')}`}
              />
            </ListItem>
          ))}
        </List>
      </Card>
    </Box>
  );
}